import { get, pick } from 'lodash-es';
import useUserContext from 'containers/User/useUserContext.js';
import useCurrentRole from 'containers/User/useCurrentRole.js';
import { useApi2, useApi2call } from 'containers/Api2/index.js';
import { makeIdObject } from 'utils/arrays.js';
import { makeUrl } from 'utils/urlUtils.js';
import setIn from 'utils/setIn.js';

const UPDATE_FIELDS = ['name', 'phone', 'email', 'websiteUrl', 'language', 'address', 'studentAvatarEnabled', 'theme'];
const PROFILE_FIELDS = ['name', 'theme'];

const ARRAY_PROPS = ['administrativeRoles', 'managerRoles', 'leadAuthors', 'dataProtectionOfficers'];

function useSchoolSettings() {
  const { updateProfile } = useUserContext();
  const role = useCurrentRole();
  const schoolId = role?.school.id;
  const url = schoolId && `sa/schools/${schoolId}/settings`;
  const fetchProps = { version: 2, withPromise: true, parser: ((o) => makeIdObject(o, ARRAY_PROPS)) };
  const settings = useApi2({ url, ...fetchProps });

  const api2call = useApi2call();

  function updateInProfile(newSettings) {
    const { name, theme } = newSettings;
    updateProfile(['roles'], (roles) => roles.list.reduce((acc, roleId) => {
      if (roles[roleId].school.id !== schoolId) {
        return acc;
      }
      const path = [roleId, 'school'];
      const currentSchool = get(acc, path);
      return setIn(acc, path, { ...currentSchool, name, displayName: name, theme });
    }, roles));
  }

  async function update(data) {
    const body = {
      ...data,
      websiteUrl: makeUrl(data.websiteUrl),
    };
    const options = { method: 'PUT', url, body, ...fetchProps, replace: false, merge: true };
    const newTheme = await api2call(options);
    if (PROFILE_FIELDS.some((key) => newTheme[key] !== settings[key])) {
      updateInProfile(newTheme);
    }
  }

  function changeDetail(key, value) {
    const body = {
      ...pick(settings, UPDATE_FIELDS),
      [key]: value,
    };
    return update(body);
  }

  function changeDetails(data) {
    const body = { ...pick(settings, UPDATE_FIELDS), ...data };
    return update(body);
  }

  function changeDpo(data) {
    const { id, ...body } = data;
    return id
      ? api2call({ method: 'PUT', url: `${url}/dataProtectionOfficers/${id}`, body, ...fetchProps })
      : api2call({ method: 'POST', url: `${url}/dataProtectionOfficers`, body, ...fetchProps });
  }

  function getFetchOptions(options) {
    return {
      ...fetchProps,
      ...options,
      url: options.url && `${url}/${options.url}`,
    };
  }

  function fetchSettings(options) {
    const requestOptions = getFetchOptions(options);
    return api2call(requestOptions);
  }

  return { settings, changeDetail, changeDetails, changeDpo, fetchSettings, getFetchOptions };
}

export default useSchoolSettings;
